import { Select } from '@components'
import { Spinner } from '@components/ui/Primitives'
import { useScheduledTriggerPreview, useWorkflowScheduledTriggerFrequencies } from '@query'
import React, { useMemo } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaArrowRight, FaGlobeEurope } from 'react-icons/fa'
import styled from 'styled-components'
import { ScheduledTriggerNodeConfig } from '../nodes/types'
import {
  buildScheduledTriggerPreviewPayload,
  DEFAULT_DAY_OF_MONTH,
  DEFAULT_DAY_OF_WEEK,
  normalizeScheduledTriggerConfigForPersist
} from './scheduledTriggerConfigUtils'
import { useWorkflowConfigForm } from './useWorkflowConfigForm'
import { formatScheduledHour } from '../nodes/scheduledTriggerConfigUtils'
import { formatScheduledRunDate } from '../nodes/scheduledTriggerSentenceUtils'
import { optionIncludesValue } from './formOptionUtils'

interface Props {
  nodeId: string
  config: ScheduledTriggerNodeConfig
  onChange: (config: ScheduledTriggerNodeConfig) => void
}

const WEEKDAY_OPTIONS = Array.from({ length: 7 }, (_, index) => index + 1)
const DAY_OPTIONS = Array.from({ length: 28 }, (_, index) => index + 1)
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => hour)

export const ScheduledTriggerConfigForm: React.FC<Props> = ({ nodeId, config, onChange }) => {
  const [t] = useTranslation()
  const { data: frequencyOptions = [] } = useWorkflowScheduledTriggerFrequencies()

  const formValues = useMemo<ScheduledTriggerNodeConfig>(
    () => ({
      frequency: config.frequency,
      hour: config.hour,
      minute: config.minute,
      day_of_week: config.day_of_week ?? null,
      day_of_month: config.day_of_month ?? null
    }),
    [config.frequency, config.hour, config.minute, config.day_of_week, config.day_of_month]
  )

  const { control, setValue, pushConfig } = useWorkflowConfigForm<ScheduledTriggerNodeConfig>({
    defaultValues: formValues,
    onChange: nextConfig => onChange(normalizeScheduledTriggerConfigForPersist(nextConfig))
  })

  const watchedConfig = useWatch({ control })
  const currentConfig: ScheduledTriggerNodeConfig = { ...formValues, ...watchedConfig }
  const previewPayload = useMemo(
    () => buildScheduledTriggerPreviewPayload(currentConfig),
    [currentConfig]
  )
  const { preview, isLoading: isPreviewLoading } = useScheduledTriggerPreview(previewPayload)
  const nextRuns = preview?.next_runs ?? []

  return (
    <>
      <Controller
        name="frequency"
        control={control}
        render={({ field, fieldState }) => (
          <Select
            id={`workflow-schedule-frequency-${nodeId}`}
            name={`workflow-schedule-frequency-${nodeId}`}
            label={t('workflows.config.schedule.fields.frequency')}
            value={field.value}
            errors={fieldState.error}
            onChange={event => {
              const nextFrequency = event.target.value
              field.onChange(nextFrequency)
              setValue(
                'day_of_week',
                nextFrequency === 'weekly' ? currentConfig.day_of_week ?? DEFAULT_DAY_OF_WEEK : null
              )
              setValue(
                'day_of_month',
                nextFrequency === 'monthly'
                  ? currentConfig.day_of_month ?? DEFAULT_DAY_OF_MONTH
                  : null
              )
              pushConfig()
            }}
            onBlur={field.onBlur}
          >
            {frequencyOptions.map(option => (
              <option key={option.value} value={option.value}>
                {t(`workflows.config.schedule.frequency.${option.value}`, {
                  defaultValue: option.label
                })}
              </option>
            ))}
            {!optionIncludesValue(frequencyOptions, field.value) && field.value && (
              <option value={field.value}>{field.value}</option>
            )}
          </Select>
        )}
      />

      {currentConfig.frequency === 'weekly' && (
        <Controller
          name="day_of_week"
          control={control}
          render={({ field, fieldState }) => (
            <Select
              id={`workflow-schedule-weekday-${nodeId}`}
              name={`workflow-schedule-weekday-${nodeId}`}
              label={t('workflows.config.schedule.fields.weekday')}
              value={String(field.value ?? DEFAULT_DAY_OF_WEEK)}
              errors={fieldState.error}
              onChange={event => {
                field.onChange(Number(event.target.value))
                pushConfig()
              }}
              onBlur={field.onBlur}
            >
              {WEEKDAY_OPTIONS.map(weekday => (
                <option key={weekday} value={weekday}>
                  {t(`workflows.config.schedule.weekday.${weekday}`)}
                </option>
              ))}
            </Select>
          )}
        />
      )}

      {currentConfig.frequency === 'monthly' && (
        <Controller
          name="day_of_month"
          control={control}
          render={({ field, fieldState }) => (
            <Select
              id={`workflow-schedule-day-${nodeId}`}
              name={`workflow-schedule-day-${nodeId}`}
              label={t('workflows.config.schedule.fields.day')}
              value={String(field.value ?? DEFAULT_DAY_OF_MONTH)}
              errors={fieldState.error}
              onChange={event => {
                field.onChange(Number(event.target.value))
                pushConfig()
              }}
              onBlur={field.onBlur}
            >
              {DAY_OPTIONS.map(day => (
                <option key={day} value={day}>
                  {t(`workflows.config.schedule.day.${day}`)}
                </option>
              ))}
            </Select>
          )}
        />
      )}

      <Controller
        name="hour"
        control={control}
        render={({ field, fieldState }) => (
          <Select
            id={`workflow-schedule-hour-${nodeId}`}
            name={`workflow-schedule-hour-${nodeId}`}
            label={t('workflows.config.schedule.fields.time')}
            value={String(field.value)}
            errors={fieldState.error}
            onChange={event => {
              field.onChange(Number(event.target.value))
              pushConfig()
            }}
            onBlur={field.onBlur}
          >
            {HOUR_OPTIONS.map(hour => (
              <option key={hour} value={hour}>
                {formatScheduledHour(hour)}
              </option>
            ))}
          </Select>
        )}
      />

      <TimezoneRow>
        <FaGlobeEurope size={11} />
        <span>
          {t('workflows.config.schedule.timezone.label')}{' '}
          <strong>{preview?.timezone ?? '—'}</strong>
        </span>
      </TimezoneRow>

      {nextRuns.length > 0 && (
        <NextRuns aria-live="polite" aria-busy={isPreviewLoading}>
          <NextRunsHeader>
            <NextRunsTitle>{t('workflows.config.schedule.nextRuns.title')}</NextRunsTitle>
            {isPreviewLoading && <Spinner size={11} />}
          </NextRunsHeader>
          <NextRunList $isStale={isPreviewLoading}>
            {nextRuns.map((run, index) => (
              <NextRunRow key={run} $isNext={index === 0}>
                <FaArrowRight size={9} />
                <NextRunTime>{formatScheduledRunDate(t, run)}</NextRunTime>
              </NextRunRow>
            ))}
          </NextRunList>
        </NextRuns>
      )}
    </>
  )
}

const TimezoneRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};

  strong {
    color: ${({ theme }) => theme.colors.metalGray};
  }
`

const NextRuns = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
`

const NextRunsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const NextRunList = styled.div<{ $isStale: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  opacity: ${({ $isStale }) => ($isStale ? 0.5 : 1)};
  transition: opacity 150ms ease;
`

const NextRunsTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.iconGray};
`

const NextRunRow = styled.div<{ $isNext: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme, $isNext }) => ($isNext ? theme.colors.metalGray : theme.colors.iconGray)};

  svg {
    color: ${({ theme, $isNext }) => ($isNext ? theme.colors.warning : theme.colors.borderStrong)};
  }
`

const NextRunTime = styled.span`
  font-family: 'Roboto Mono', monospace;
`
